import { BrandMark } from "./BrandMark.js";
import { Icon } from "./Icon.js";
import { useTheme } from "../lib/theme.js";

/**
 * The signed-out shell (D204).
 *
 * Rebuilt on the monochrome palette. The eyebrow prop is kept in the signature
 * because three screens pass one, but it renders as quiet supporting text now
 * rather than an uppercase letterspaced label.
 *
 * Deliberately does *not* import the shadcn `Button`: `App` renders this frame
 * for its loading and error states, so it is on the eager path, and `cn` drags
 * `tailwind-merge` — 105 KB — into an entry chunk the build caps at 200 KB
 * (D204). One plain button is the cheaper half of that trade.
 */
export const AuthFrame = ({
  eyebrow,
  title,
  description,
  children,
}: {
  readonly eyebrow: string;
  readonly title: string;
  readonly description: string;
  readonly children: React.ReactNode;
}): React.JSX.Element => {
  const theme = useTheme();

  return (
    <main className="auth-frame">
      <section className="auth-story" aria-label="Vakilat trust statement">
        <div className="flex items-center gap-3">
          <BrandMark />
          <span className="text-lg font-semibold">Vakilat</span>
        </div>
        <div className="max-w-lg">
          <h1 className="text-3xl leading-tight font-semibold tracking-tight sm:text-4xl">
            Legal work that shows its sources.
          </h1>
          <p className="mt-4 max-w-md text-base leading-7 text-[var(--muted)]">
            Research Indian law, draft from real authority, and inspect every step
            before anything leaves your practice.
          </p>
        </div>
        <p className="text-sm text-[var(--dim)]">Processed in India</p>
      </section>

      <section className="auth-panel">
        <button
          type="button"
          onClick={theme.toggle}
          className="icon-button absolute top-5 right-5"
          aria-label={`Use ${theme.mode === "dark" ? "light" : "dark"} theme`}
        >
          <Icon name={theme.mode === "dark" ? "sun" : "moon"} size={17} />
        </button>
        <div className="w-full max-w-md">
          <p className="text-sm text-[var(--dim)]">{eyebrow}</p>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight">{title}</h2>
          <p className="mt-2 text-sm leading-6 text-[var(--muted)]">{description}</p>
          <div className="mt-8">{children}</div>
        </div>
      </section>
    </main>
  );
};
